import { useCallback, useContext, createContext, ReactNode, useState, FC } from 'react'
import { uuidv4 } from 'utils/generic'

interface ModalData {
  id: string
  content: ReactNode
  stack?: string
  stackIndex?: number
}

type ModalOptions = {
  id?: string
  stack?: string
}

interface ScopeModalContextType {
  modals: ModalData[]
  setModal: (modal: ReactNode | null, options?: ModalOptions) => void
  resetModals: () => void
  setCloseModal: (id: string) => void
  setCloseStack: (stack: string) => void
}

const ScopeModalContext = createContext<ScopeModalContextType | null>(null)

export const ScopeModalProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [modals, setModals] = useState<ModalData[]>([])

  const isTopModalInStack = useCallback(
    (currentModal: ModalData): boolean => {
      if (!currentModal.stack) return true

      const stackModals = modals.filter((item) => item.stack === currentModal.stack)
      const maxIndex = stackModals.reduce(
        (max, item) => (typeof item.stackIndex === 'number' ? Math.max(max, item.stackIndex) : max),
        -1,
      )

      return currentModal.stackIndex === maxIndex
    },
    [modals],
  )

  const setModal = useCallback((content: ReactNode | null, options?: ModalOptions) => {
    const id = options?.id ?? 'default'
    const stack = options?.stack

    // If stack specified, add new one with stack index
    if (stack) {
      setModals((prev) => {
        // find max index in stack
        const stackModals = prev.filter((item) => item.stack === stack)
        const maxIndex = stackModals.reduce(
          (max, item) => (typeof item.stackIndex === 'number' ? Math.max(max, item.stackIndex) : max),
          -1,
        )
        const stackIndex = maxIndex + 1

        return [
          ...prev,
          {
            id,
            content,
            stack,
            stackIndex,
          },
        ]
      })
      return
    }

    setModals((prev) => {
      const existingModalID = prev.findIndex((item) => item.id === id)
      if (existingModalID >= 0) {
        if (content === null || content === undefined) {
          return prev.filter((item) => item.id !== id)
        }

        return prev.map((item) => (item.id === id ? { ...item, content } : item))
      }

      return [...prev, { id, content }]
    })
  }, [])

  const setCloseModal = useCallback((id: string) => {
    setModals((prev) => prev.filter((modal) => modal.id !== id))
  }, [])

  const setCloseStack = useCallback((stack: string) => {
    setModals((prev) => prev.filter((item) => item.stack !== stack))
  }, [])

  const resetModals = useCallback(() => {
    setModals([])
  }, [])

  return (
    <ScopeModalContext.Provider
      value={{
        modals,
        setModal,
        resetModals,
        setCloseModal,
        setCloseStack,
      }}
    >
      {children}
      {modals.map((modal) => {
        const hiddenByStack = modal.stack ? !isTopModalInStack(modal) : false
        return (
          <div key={modal.id} style={{ display: hiddenByStack ? 'none' : undefined }}>
            {modal.content}
          </div>
        )
      })}
    </ScopeModalContext.Provider>
  )
}

export const useScopeModal = (defaultOptions?: ModalOptions) => {
  const context = useContext(ScopeModalContext)

  if (!context) {
    throw new Error('useScopeModal must be used within a ScopeModalProvider')
  }

  const defaultID = uuidv4()

  return {
    ...context,
    setModal: (content: ReactNode | null, options?: ModalOptions) => {
      context.setModal(content, { ...defaultOptions, ...options, id: options?.id ?? defaultID })
    },
    setCloseModal: (id?: string) => {
      context.setCloseModal(id ?? defaultID)
    },
  }
}

export default useScopeModal
